import { useState } from 'react'
import { useAdminApi } from '../api'
import { t } from '../i18n'
import { useLang } from '../LangContext'
import { formatDate, localeFor } from '../../utils/format'
import { Icon } from '../../styles/Icon'
import styles from './MiembrosTable.module.css'

export interface Miembro {
  id: string
  persona: { nombre: string }
  grupo: string
  fecha_incorporacion: string
  fecha_baja: string | null
  orden: number
}

export function MiembrosTable({
  equipoId,
  miembros,
  onChange,
}: {
  equipoId: string
  miembros: Miembro[]
  onChange: () => void
}) {
  const api = useAdminApi()
  const lang = useLang()
  const locale = localeFor(lang)
  const [dragId, setDragId] = useState<string | null>(null)

  async function handleDrop(targetId: string) {
    if (!dragId || dragId === targetId) return

    const ordered = [...miembros]
    const fromIndex = ordered.findIndex((m) => m.id === dragId)
    const toIndex = ordered.findIndex((m) => m.id === targetId)
    const [moved] = ordered.splice(fromIndex, 1)
    ordered.splice(toIndex, 0, moved)

    await Promise.all(
      ordered.map((miembro, index) =>
        api.patch(`/admin/equipos/${equipoId}/miembros/${miembro.id}`, { orden: index }),
      ),
    )
    setDragId(null)
    onChange()
  }

  async function handleBaja(miembroId: string) {
    const fecha = window.prompt(t('fechaBaja', lang))
    if (!fecha) return
    await api.patch(`/admin/equipos/${equipoId}/miembros/${miembroId}`, { fecha_baja: fecha })
    onChange()
  }

  async function handleDelete(miembroId: string) {
    if (!window.confirm(t('borrarMiembroConfirm', lang))) return
    await api.del(`/admin/equipos/${equipoId}/miembros/${miembroId}`)
    onChange()
  }

  // Issue #8 del vídeo: el estado "con ficha"/"sin ficha" no se podía cambiar tras el alta, solo al
  // importar. Los entrenadores no se tocan aquí (cambiar su grupo es una acción distinta).
  async function handleGrupoChange(miembroId: string, grupo: string) {
    await api.patch(`/admin/equipos/${equipoId}/miembros/${miembroId}`, { grupo })
    onChange()
  }

  return (
    <div className={styles.tableWrap}>
      <table className="cards-on-mobile sticky-first-col">
        <thead>
          <tr>
            <th>{t('nombre', lang)}</th>
            <th>{t('grupo', lang)}</th>
            <th>{t('fechaIncorporacion', lang)}</th>
            <th>{t('fechaBaja', lang)}</th>
            <th>{t('orden', lang)}</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {miembros.map((miembro) => (
            <tr
              key={miembro.id}
              draggable
              onDragStart={() => setDragId(miembro.id)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => handleDrop(miembro.id)}
            >
              <td data-label={t('nombre', lang)}>{miembro.persona.nombre}</td>
              <td data-label={t('grupo', lang)}>
                {/* Issue #80: el rol (jugador con/sin ficha, entrenador, delegado) se cambia sin borrar
                    a la persona; antes los entrenadores no se podían cambiar desde aquí. */}
                <select
                  aria-label={t('grupo', lang)}
                  value={miembro.grupo}
                  onChange={(e) => handleGrupoChange(miembro.id, e.target.value)}
                >
                  <option value="con_ficha">{t('conFichaOpcion', lang)}</option>
                  <option value="sin_ficha">{t('sinFichaOpcion', lang)}</option>
                  <option value="entrenador">{t('entrenadorOpcion', lang)}</option>
                  <option value="delegado">{t('delegadoOpcion', lang)}</option>
                </select>
              </td>
              <td data-label={t('fechaIncorporacion', lang)}>{formatDate(miembro.fecha_incorporacion, locale)}</td>
              <td data-label={t('fechaBaja', lang)}>{miembro.fecha_baja ? formatDate(miembro.fecha_baja, locale) : '—'}</td>
              <td data-label={t('orden', lang)}>{miembro.orden}</td>
              <td>
                {!miembro.fecha_baja && <button onClick={() => handleBaja(miembro.id)}>{t('fechaBaja', lang)}</button>}
                <button onClick={() => handleDelete(miembro.id)}><Icon name="eliminar" />{t('eliminar', lang)}</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
